/**
 * This component wraps React-Router's Link by providing name-based routing.
 *
 * The `name` prop should match a route in the flattened
 * routeConfiguration passed in context by the RoutesProvider
 * component. The `params` props is the route params for the route
 * path of the given route name.
 *
 * The `to` prop is an object with the same shape as Link requires,
 * but without `pathname` that will be generated from the given route
 * name.
 *
 * Some additional props can be passed for the <a> element like
 * `className` and `style`.
 *
 * The component can also be given the `activeClassName` prop that
 * will be added to the element className if the current URL matches
 * the one in the generated pathname of the link.
 */
import React from 'react';
import { object, string, shape, any } from 'prop-types';
import { Link, withRouter } from 'react-router-dom';
import classNames from 'classnames';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { pathByRouteName, findRouteByRouteName } from '../../util/routes';

export const NamedLinkComponent = props => {
  const routeConfiguration = useRouteConfiguration();
  const { name, params, title } = props;

  const onOver = e => {
    const { component: Page } = findRouteByRouteName(name, routeConfiguration);
    // Loadable Component has a "preload" function.
    if (Page.preload) {
      Page.preload();
    }
    // Forward to caller's handler if provided
    if (typeof props.onMouseOver === 'function') props.onMouseOver(e);
  };
  const onTouch = e => {
    const { component: Page } = findRouteByRouteName(name, routeConfiguration);
    if (Page.preload) Page.preload();
    if (typeof props.onTouchStart === 'function') props.onTouchStart(e);
  };

  // Link props
  const { to, children } = props;
  const pathname = pathByRouteName(name, routeConfiguration, params);
  const { match } = props;
  const active = match.url && match.url === pathname;

  // <a> element props — also forward pointer/focus handlers so callers can
  // react to hover (e.g. cross-fading a hero background behind the card).
  const { className, style, activeClassName, onMouseEnter, onMouseLeave, onFocus, onBlur } = props;
  const aElemProps = {
    className: classNames(className, { [activeClassName]: active }),
    style,
    title,
    /* These two were being dropped. This component builds an explicit allowlist
     * of <a> props rather than spreading the rest, so anything not listed here
     * silently never reaches the DOM — and several callers had been passing
     * `aria-label` and `data-tooltip` for a long time believing they worked.
     *
     * The visible cost: the topbar's envelope, heart and cart icons are
     * icon-only links, so with no accessible name a screen reader announced them
     * as just their href. The topbar's CSS tooltips (`[data-tooltip]`) never
     * appeared either. P25009-390 BR-6 makes this load-bearing — the envelope's
     * label is what says "3 olästa" to anyone not looking at the badge.
     *
     * Still an allowlist, deliberately: a blanket `...rest` would push `name`,
     * `params` and `activeClassName` onto the anchor and React would warn about
     * every one. */
    'aria-label': props['aria-label'],
    'data-tooltip': props['data-tooltip'],
    onMouseEnter,
    onMouseLeave,
    onFocus,
    onBlur,
  };

  return (
    <Link onMouseOver={onOver} onTouchStart={onTouch} to={{ pathname, ...to }} {...aElemProps}>
      {children}
    </Link>
  );
};

NamedLinkComponent.defaultProps = {
  params: {},
  to: {},
  children: null,
  className: '',
  style: {},
  activeClassName: 'NamedLink_active',
  title: null,
  match: {},
};

// This ensures a nice display name in snapshots etc.
NamedLinkComponent.displayName = 'NamedLink';

NamedLinkComponent.propTypes = {
  // name of the route in routeConfiguration
  name: string.isRequired,
  // params object for the named route
  params: object,
  // Link component props
  to: shape({ search: string, hash: string, state: object }),
  children: any,

  // generic props for the underlying <a> element
  className: string,
  style: object,
  activeClassName: string,
  title: string,

  // from withRouter
  match: object,
};

const NamedLink = withRouter(NamedLinkComponent);
NamedLink.displayName = 'NamedLink';

export default NamedLink;
